import * as React from 'react';

import { Box } from '@mui/system';
import styled from 'styled-components';
import TextField from '@mui/material/TextField';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Slider from '@mui/material/Slider';
import Button from '@mui/material/Button';
import { useState, FormEvent } from 'react';
import MenuItem from '@mui/material/MenuItem';
import { NumericFormat } from 'react-number-format';
import InputAdornment from '@mui/material/InputAdornment';

import {
  MainContainer,
  CalculatorContainer,
  ResultsContainer,
  DisclaimerContainer,
  ResultsMainContainer,
  ResultsTitle,
  ResultsSection,
} from '../styles/calculator.styles';

interface CalcSections extends sharePercent {
  fullMarketValue: number;
  term: any;
  sharePercentage: any;
  depositAmount: number;
  shareValue: number;
  mortgageRate: number;
}

interface sharePercent {
  sharePercentage: any;
}

const CustomSliderStyles = {
  '& .MuiSlider-thumb': {
    '&:focus, &:hover, &.Mui-active': {
      boxShadow: '0px 0px 3px 1px rgba(0, 0, 0, 0.1)',
      '@media (hover: none)': {
        boxShadow: 0,
      },
    },
    '&:before': {
      boxShadow:
        '0px 0px 1px 0px rgba(0,0,0,0.2), 0px 0px 0px 0px rgba(0,0,0,0.14), 0px 0px 1px 0px rgba(0,0,0,0.12)',
    },

    color: '#407C7D',
    border: '2px solid #FFC09C',
  },
  '& .Mui-focusVisible': {},

  '& .MuiSlider-track': {
    color: '#407C7D',
  },
  '& .MuiSlider-rail': {
    color: '#407C7D',
  },
  '& .MuiSlider-active': {
    color: '#407C7D',
  },
  '& .MuiSlider-valueLabelCircle': {
    '& :after': {
      content: '"%"',
      width: '10px',
    },
  },
  '& .MuiSlider-valueLabel': {
    background: 'none',
    fontWeight: 'bold',
    color: '#696969',
    margin: '9px 0 0 0 ',
  },
};

const S = {
  submitButton: styled(Button)`
    ${({ theme: { breakpoints, colors } }) => `
    background-color: ${colors.greens.green0};
    width:60%;
    border-radius:25px;
    margin: 0 auto;
    `}
  `,

  percentageContainer: styled(Box)<sharePercent>`
    [data-id='percent'] {
      font-weight: bold;
    }
    display: flex;
    justify-content: space-between;
    & p:first-child {
      visibility: ${({ sharePercentage }) =>
        sharePercentage === 25 ? 'hidden' : 'visible'};
    }
    & p:nth-child(2) {
      visibility: ${({ sharePercentage }) =>
        sharePercentage === 75 ? 'hidden' : 'visible'};
    }
  `,

  label: styled.p`
    ${({ theme: { breakpoints, colors } }) => `
    color:  ${colors.greys.greys25};
    font-size: 14px;
    margin: 10px 0 0 0;
    `}
  `,

  textFields: styled(TextField)`
    .MuiOutlinedInput-notchedOutline {
      border: 0;
    }
    ${({ theme: { breakpoints, colors } }) => `
    border-radius: 0px !important;
    margin-bottom: 10px;

   border: 0;
   .MuiInputBase-root {  border-radius: 0px !important;}
    input {
      color: #508788;
      border: 1px solid #407C7D;
      border-radius: 0px !important;
    }
   
    #shareValue {
      background-color: #407c7d;
      color: ${colors.whites.white1};
      .MuiOutlinedInput-notchedOutline {border: 0;}
    }

    `}
  `,

  numberFields: styled(NumericFormat)`
    color: #508788 !important;
    border: 1px solid #407c7d !important;
    border-radius: 0px !important;
    padding: 16.5px 14px;
    font-size: 16px;
    &::focus-visible {
      border: 1px solid #407c7d !important;
      border-radius: 0px !important;
    }
    .MuiOutlinedInput-notchedOutline {
      border: 0;
    }
    ${({ theme: { breakpoints, colors } }) => `
        border-radius: 0px !important;x
        margin-bottom: 10px;
    
       border: 0;

       .MuiInputBase-root {  border-radius: 0px !important;}
        input {
          color: #508788;
          border: 1px solid #407C7D;
          border-radius: 0px !important;
        }
       
        #shareValue {
          background-color: #407c7d;
          color: ${colors.whites.white1};
          .MuiOutlinedInput-notchedOutline {border: 0;}
        }
    
        `}
  `,
};

type Props = {
  data: Record<string, any>;
};

const Calculator = (props: Props) => {
  function valuetext(value: number) {
    return `${value}`;
  }

  const [state, setState] = useState<CalcSections>({
    fullMarketValue: 240000,
    term: 25,
    shareValue: 108000,
    sharePercentage: 45,
    depositAmount: 5400,
    mortgageRate: 5.5,
  });
  const onSliderChange = (event: any) => {
    setValue(event.target.value);
    setState({ ...state, sharePercentage: event.target.value });
  };

  const onSelectChange = (event: SelectChangeEvent) => {
    const values = event.target.value;
    setState({ ...state, term: +values });
  };

  const onFieldChange = (event: any) => {
    const value =
      event.target.value < 0 ? (event.target.value = 1) : event.target.value;
    setState({ ...state, [event.target.id]: value });
  };

  const onDepositFieldChange = (event: any) => {
    const value =
      event.target.value < 0 ? (event.target.value = 1) : event.target.value;

    value;
    setState({ ...state, [event.target.id]: value.slice(0, 5) });
  };

  let GBPound = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'GBP',
  });

  let fullMarketValue = state.fullMarketValue
    .toString()
    .replace(/,/g, '')
    .replace('£', '');
  let term = state.term;
  let period = 12;

  let shareValue = state.shareValue;
  let sharePercentage = state.sharePercentage;
  let unsoldSharedPercentage = 75 - state.sharePercentage;
  let mortgageRate = state.mortgageRate;
  let depositAmount = state.depositAmount
    .toString()
    .replace(/,/g, '')
    .replace('£', '');
  let shareValueAmount = (sharePercentage / 100) * +fullMarketValue;
  let balanceAmount = shareValueAmount - +depositAmount;
  let equity = +fullMarketValue - shareValueAmount;
  let unsoldEquity = +fullMarketValue - shareValueAmount;
  let rentPercentge = 2.75;

  let monthlyRent = (unsoldEquity / 100) * (rentPercentge / period);

  let N = term * period;
  let I = mortgageRate / 100 / period;
  let v = Math.pow(1 + I, N);
  let t = (I * v) / (v - 1);
  let result = balanceAmount * t;
  const [value, setValue] = React.useState<number>(60);

  let monthlyTotal = result + monthlyRent;

  return (
    <MainContainer>
      <CalculatorContainer>
        <S.label>Full Market Value</S.label>

        <S.numberFields
          className="NumericArea"
          value={
            state.fullMarketValue !== null ? state.fullMarketValue : 250000
          }
          thousandSeparator
          prefix="£"
          // customInput={TextField}
          id="fullMarketValue"
          onChange={onFieldChange}
        />

        <S.label>Term</S.label>
        <Select
          sx={{
            border: '1px solid #407C7D',
            borderRadius: '0 !important',
            color: '#508788',
          }}
          labelId="term"
          id="term"
          value={state.term !== null ? state.term : 25}
          onChange={onSelectChange}
        >
          <MenuItem value={25}>25 Years</MenuItem>
          <MenuItem value={30}>30 Years</MenuItem>
          <MenuItem value={35}>35 Years</MenuItem>
        </Select>

        <S.label>Share Value</S.label>

        <S.numberFields
          thousandSeparator
          // customInput={TextField}
          id="shareValue"
          prefix="£"
          value={
            shareValueAmount !== null ? shareValueAmount.toFixed(2) : 108000
          }
        />

        <S.label>Share Percentage</S.label>
        <S.percentageContainer sharePercentage={state.sharePercentage}>
          <S.label data-id="percent">25%</S.label>
          <S.label data-id="percent">75%</S.label>
        </S.percentageContainer>
        <Slider
          sx={CustomSliderStyles}
          value={value !== null ? value : 45}
          min={25}
          step={5}
          max={75}
          valueLabelDisplay="on"
          onChange={onSliderChange}
          aria-label="Always visible"
          aria-labelledby="non-linear-slider"
          getAriaValueText={valuetext}
        />
        <S.label>Deposit Amount</S.label>

        <S.numberFields
          value={state.depositAmount !== null ? state.depositAmount : 5400}
          thousandSeparator
          prefix="£"
          // customInput={TextField}
          id="depositAmount"
          onChange={onDepositFieldChange}
        />

        <S.label>Mortgage Rate %</S.label>
        <S.textFields
          type="number"
          inputProps={{ step: '.5' }}
          id="mortgageRate"
          onChange={onFieldChange}
          value={state.mortgageRate !== null ? state.mortgageRate : 5.5}
        />
      </CalculatorContainer>
      <ResultsMainContainer>
        <ResultsContainer>
          <ResultsTitle>Results</ResultsTitle>
          <ResultsSection>
            Monthly Cost: <strong>{GBPound.format(monthlyTotal)}</strong>
          </ResultsSection>
          <ResultsSection>
            Mortgage: <strong>{GBPound.format(result)}</strong>
          </ResultsSection>
          <ResultsSection>
            Rent: <strong>{GBPound.format(monthlyRent)}</strong>
          </ResultsSection>
        </ResultsContainer>
        <DisclaimerContainer>
          <p>Disclaimer</p>
          <div dangerouslySetInnerHTML={{ __html: props.data.content }}></div>
        </DisclaimerContainer>
      </ResultsMainContainer>
    </MainContainer>
  );
};

export default Calculator;
